import React from 'react';

import { ActionIcon, Button, Group, Paper, Progress, Stack, Text, Title } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { useAuth } from 'context/auth-context';
import useDismissOnboardingBanner from 'hooks/gym/useDismissOnboardingBanner';
import useOnboardingChecklist from 'hooks/gym/useOnboardingChecklist';
import { useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import { useNavigate } from 'react-router-dom';

const INCOMPLETE_TITLE = "Complete your gym's setup";
const INCOMPLETE_SUBTITLE =
  'Complete the onboarding checklist to unlock scheduling, payments, and staff access before your first class.';
const COMPLETE_TITLE = "You're all set";
const COMPLETE_SUBTITLE = 'Every onboarding step is complete. Your gym is ready to go.';

/**
 * The dashboard setup banner.
 *
 * Whether it renders is derived from progress and the stored dismissal, never from a third
 * stored flag: see `useOnboardingChecklist`. All three CTAs go to the same checklist page;
 * only the label changes.
 */
const OnboardingChecklistBanner = ({ className }: { className?: string }): JSX.Element | null => {
  const navigate = useNavigate();
  const { session } = useAuth();
  const { trackOnboardingChecklistBannerDismissed } = useStaffTracking();
  const { completedCount, totalCount, isComplete, shouldRenderBanner, gymId, isLoading, isError } =
    useOnboardingChecklist();
  const { mutate: dismissBanner, isLoading: isDismissing } = useDismissOnboardingBanner(gymId);

  if (isLoading || isError || !shouldRenderBanner) {
    return null;
  }

  const canDismiss = isComplete && session?.role === 'owner';

  const ctaLabel = isComplete
    ? 'View checklist'
    : completedCount === 0
    ? 'Begin setup →'
    : 'Continue setup →';

  const handleDismiss = (): void => {
    dismissBanner(undefined, {
      onSuccess: () => trackOnboardingChecklistBannerDismissed({ steps_completed: completedCount }),
    });
  };

  return (
    <Paper className={className} withBorder radius='md' p='lg' data-cy='onboardingChecklistBanner'>
      <Group position='apart' align='flex-start' noWrap>
        <Stack spacing={4} sx={{ flexGrow: 1 }}>
          <Title order={2} size='h3'>
            {isComplete ? COMPLETE_TITLE : INCOMPLETE_TITLE}
          </Title>
          <Text color='dimmed' size='sm'>
            {isComplete ? COMPLETE_SUBTITLE : INCOMPLETE_SUBTITLE}
          </Text>

          {!isComplete && (
            <Group spacing='md' align='center' noWrap mt='xs'>
              <Progress
                value={(completedCount / totalCount) * 100}
                size='lg'
                radius='xl'
                sx={{ flexGrow: 1 }}
                data-cy='onboardingChecklistProgress'
              />
              <Text size='sm' color='dimmed' sx={{ whiteSpace: 'nowrap' }}>
                {completedCount} of {totalCount} steps complete
              </Text>
            </Group>
          )}
        </Stack>

        <Group spacing='xs' align='center' noWrap>
          <Button onClick={() => navigate('/onboarding-checklist')} data-cy='onboardingChecklistCta'>
            {ctaLabel}
          </Button>
          {canDismiss && (
            <ActionIcon
              variant='subtle'
              aria-label='Hide the setup banner'
              loading={isDismissing}
              onClick={handleDismiss}
              data-cy='onboardingChecklistDismiss'
            >
              <IconX size={18} />
            </ActionIcon>
          )}
        </Group>
      </Group>
    </Paper>
  );
};

export default OnboardingChecklistBanner;
